import G6, { TreeGraph } from '@antv/g6/dist/g6.min';
import { activityTypeOptions, fullOptionsMap, fullOptionsValueMap } from '../options';
import { generateActionBar } from './common';
import {
  conditionContentPaddingY,
  conditionRowHeight,
  conditionRowInnerHeight,
  conditionTitleHeight,
  CustomNodeType,
  splitLineHeight,
  splitLineMargin,
  textareaHeight
} from './config';
import { cloneDeep } from 'lodash-es';
import Decimal from 'decimal.js';

function changeEvtfn(e) {
  const target = e.target;
  const changeEvtfnMap = {
    // 活动类型
    'activity-type-select': 'onActivityTypeChange',
    // 叠加活动优先级
    'activity-diejia-select': 'onActivityTypeDiejiaChange'
  };

  for (const key in changeEvtfnMap) {
    if (target.classList.contains(key)) {
      const id = target.getAttribute('data-id');
      const index = target.getAttribute('data-index');

      // @ts-ignore
      window[changeEvtfnMap[key]](id, e.target, index);
    }
  }
}

function inputEvtfn(e) {
  const target = e.target;

  const inputEvtfnMap = {
    // 优先级
    'activity-type-priority-input': 'onActivityPriorityInput'
  };

  for (const key in inputEvtfnMap) {
    if (target.classList.contains(key)) {
      const id = target.getAttribute('data-id');

      // @ts-ignore
      window[inputEvtfnMap[key]](id, e.target);
    }
  }
}

function blurEvtfn(e) {
  const target = e.target;

  const blurEvtfnMap = {
    // 优先级
    'activity-type-priority-input': 'onActivityPriorityBlur'
  };

  for (const key in blurEvtfnMap) {
    if (target.classList.contains(key)) {
      const id = target.getAttribute('data-id');

      // @ts-ignore
      window[blurEvtfnMap[key]](id, e.target);
    }
  }
}

export const generateActivityRuleStr = (activityJsonData) => {
  const {
    optionType,
    hasPrecondition,
    conditions,
    conditionsCombine,
    effectConditions,
    effectConditionsCombine,
    equityInformationRule
  } = activityJsonData;

  const linkMap = {
    or: '或',
    and: '且'
  };

  // 构建活动规则文案，整体文案为：[[(前置条件1) 且 (前置条件2)] 且 [(前置条件3) 或 (前置条件4)]] 且 [[(门槛条件1) 且 (门槛条件2)] 且 [(门槛条件3) 或 (门槛条件4)]]，活动权益
  // 其中前置条件、门槛条件不唯一时以()容纳单一条件，如果是多层嵌套，则以[] 容纳，以 且/或 连接多条件，且/或 前后追加 空格，()[] 为半角。前置条件文案及门槛条件文案之间以 且 拼接，门槛条件文案后拼接 ，，再拼接权益信息文案
  let activityRuleStr = '';

  // 根据 conditionsCombine 找到对应条件id拼接前置条件，or转为或，and转为且
  if (hasPrecondition) {
    let preConditionsStr = '';
    conditionsCombine.groups.forEach((group, groupIndex) => {
      let groupStr = '';
      group._uniqueIds.forEach((id, index) => {
        const condition = conditions.find((c) => c._uniqueId === id);

        // 前置条件如：前置条件文案：X 件/中包/PCS{取单位的显示名称} 起订，必定{串联文案} X 款/件/元；
        const unitValue = condition[fullOptionsValueMap[condition.optionType]];
        const unitOption = fullOptionsMap[condition.optionType];
        groupStr += `(${condition.optionValue} ${condition.orderUnit} 起订，必定 ${condition.fullNumber} ${
          unitOption.find((i) => i.value === unitValue)?.label
        }) ${linkMap[group.type]} `;

        if (index === group._uniqueIds.length - 1) {
          // 删除最后一个连接符
          if (group._uniqueIds.length === 1) {
            groupStr = groupStr.substring(0, groupStr.length - 3);
          } else {
            groupStr = `[${groupStr.substring(0, groupStr.length - 3)}]`;
          }
        }
      });

      preConditionsStr += `${groupStr} ${linkMap[conditionsCombine.type]} `;

      if (groupIndex === conditionsCombine.groups.length - 1) {
        // 删除最后一个连接符
        preConditionsStr = preConditionsStr.substring(0, preConditionsStr.length - 3);
      }
    });

    activityRuleStr += `[${preConditionsStr}] 且 `;
  }
  // 门槛规则
  let conditionStr = '';
  effectConditionsCombine.groups.forEach((group, groupIndex) => {
    let groupStr = '';
    group._uniqueIds.forEach((id, index) => {
      const condition = effectConditions.find((c) => c._uniqueId === id);

      // 门槛条件如：X 件/中包/PCS{取单位的显示名称} 起订，订满{串联文案} X 款/件/元；
      const unitValue = condition[fullOptionsValueMap[condition.optionType]];
      const unitOption = fullOptionsMap[condition.optionType];
      groupStr += `(${condition.optionValue} ${condition.orderUnit} 起订，订满 ${condition.fullNumber} ${
        unitOption.find((i) => i.value === unitValue)?.label
      }) ${linkMap[group.type]} `;

      if (index === group._uniqueIds.length - 1) {
        // 删除最后一个连接符
        if (group._uniqueIds.length === 1) {
          groupStr = groupStr.substring(0, groupStr.length - 3);
        } else {
          groupStr = `[${groupStr.substring(0, groupStr.length - 3)}]`;
        }
      }
    });

    conditionStr += `${groupStr} ${linkMap[effectConditionsCombine.type]} `;

    if (groupIndex === effectConditionsCombine.groups.length - 1) {
      // 删除最后一个连接符
      conditionStr = conditionStr.substring(0, conditionStr.length - 3);
    }
  });

  activityRuleStr += `${conditionStr ? `[${conditionStr}]，` : ''}`;

  // 权益信息文案：
  const { reductionOption, giftOption, reductionDiscount, giftDiscount, discountValue, giftItemStr } =
    equityInformationRule;
  let equityInfoStr = '';

  const giftOptionMap = {
    TARGET: '指定赠品 *X',
    CHOOSE_SELF: '赠品自选*X',
    ITEM_FULL_A_TO_A: '料号满 A 赠 A *X',
    ITEM_FULL_A_TO_B: '料号 A 赠 B *X',
    ARTICLE_FULL_A_TO_A: '货号满 A 赠 A *X',
    ARTICLE_FULL_A_TO_B: '货号 A 赠 B *X'
  };

  // 满减：让利 X 元/%
  if (optionType === 'FULL_SUB') {
    if (reductionOption === 'REDUCTION_AMOUNT') {
      equityInfoStr += `让利 ${discountValue} 元`;
      // 循环
      if (reductionDiscount === 'CYCLIC_FULL_DECREMENT') {
        equityInfoStr = `循环${equityInfoStr}`;
      }
    } else if (reductionOption === 'DISCOUNT_RATE') {
      //使用times解决精度问题
      equityInfoStr += `让利 ${new Decimal(1).minus(new Decimal(discountValue?discountValue:0)).times(100).toNumber()}%`;
    }
  }
  // 满赠：
  else if (optionType === 'FULL_GWP') {
    // 赠送一次：指定赠品 *X / 赠品自选*X / 料号满 A 赠 A *X / 货号满 A 赠 A *X / 料号 A 赠 B *X / 货号 A 增 B *X
    equityInfoStr += giftOptionMap[giftOption].replace('X', discountValue);
    // 每满赠：每满赠指定赠品 *X / 赠品自选*X / 料号满 A 赠 A *X / 货号满 A 赠 A *X / 料号 A 赠 B *X / 货号 A 增 B *X
    if (giftDiscount === 'FULL_QUANTITY') {
      equityInfoStr = `每满赠-${equityInfoStr}`;
    }
  }
  // 直降：享受直降价
  else if (optionType === 'DIRECT_DESCENT') {
    equityInfoStr += '享受直降价';
  } else if (optionType === 'SECKILL') { 
    equityInfoStr += '秒杀';
  }

  activityRuleStr += equityInfoStr;

  return activityRuleStr;
};

export const registerActivityTypeShape = (rowData) => {
  
  G6.registerNode(CustomNodeType.ACTIVITY_TYPE, {
    draw: (cfg, group) => {
      let selects: any[] = [];
      let options: any[] = [];
      const jsonData = rowData[cfg.dataId];
      
      // 自身是从活动时禁止选择 自身是秒杀活动时不与任何活动叠加也不能叠加任何活动
      if (jsonData.activityAllPriority?.[cfg.id]?.isSub||jsonData.optionType === 'SECKILL') {
        selects = [];
      }
      // 过滤选项
      else {
        const setVal = (jsonData.promotionSuperpositionPriority || '').split('#').filter((i) => i);
        const allPriorities = Object.values(jsonData.activityAllPriority || {}) as any[];
        const isCurrentFull = ['FULL_SUB', 'FULL_GWP'].includes(jsonData.optionType);

        // 不能与自己叠加
        // 满减/满赠不能选择直降进行叠加
        // 直降只能选择满赠进行叠加
        selects = allPriorities.filter(
          (i: any) =>
            !i.isMain &&
            i.id !== cfg.id &&
            (isCurrentFull
              ? rowData[i.dataId].optionType !== 'DIRECT_DESCENT'&&rowData[i.dataId].optionType !== 'SECKILL'
              : rowData[i.dataId].optionType === 'FULL_GWP')
        );

        options = cloneDeep(selects);

        // options.forEach((i: any) => {
        //   const isValueRepeat = setVal.includes(i.value);
        //   const optionType = rowData[i.dataId].optionType;
        //   // 不能重复叠加同一活动
        //   // 满减/满赠不能选择直降进行叠加
        //   // 直降只能选择一个满赠进行叠加
        //   if (
        //     isValueRepeat ||
        //     (isCurrentFull && optionType === 'DIRECT_DESCENT') ||
        //     (!isCurrentFull && (setVal.length == 1 || optionType !== 'FULL_GWP'))
        //   ) {
        //     i.disabled = true;
        //   }
        // });

        // 直降只能选择一个满赠进行叠加
        const hasFullGwp = allPriorities.some((i) => rowData[i.dataId].optionType === 'FULL_GWP');
        if (!isCurrentFull && hasFullGwp) {
          selects = [selects[0]];
        }
      }

      const diejiaSelects = `
      <span>可叠加:</span>
      ${selects.reduce((ta, a: any, index) => {
        return (
          ta +
          `
          ${index > 0 ? '<span>-</span>' : ''}
          <select data-index="${index}" data-id="${
            cfg!.id
          }" class="activity-diejia-select condition-select" style="height: ${conditionRowInnerHeight}px;width: 60px;" placeholder="请选择">
            <option value="">无</option>
          ${options.reduce(
            (t, c: any) =>
              t +
              `<option value="${c.value}" ${
                jsonData?.promotionSuperpositionPriority?.split('#')?.[index] === c.value ? 'selected' : ''
              } ${c.disabled ? 'disabled' : ''}>${c.value}</option>`,
            ''
          )}
          </select>`
        );
      }, '')}
      `;

      const conditionHtml = `
      <div style="display: flex;align-items: center;column-gap: 4px;height: ${conditionRowHeight}px">
        <select data-id="${
          cfg!.id
        }" class="activity-type-select condition-select" style="height: ${conditionRowInnerHeight}px;" placeholder="">
        ${activityTypeOptions.reduce(
          (t, c) =>
            t + `<option value="${c.value}" ${jsonData.optionType == c.value ? 'selected' : ''}>${c.label}</option>`,
          ''
        )}
        </select>
        <span>优先级:</span>
        <input id="input_${cfg!.id}" data-id="${
        cfg!.id
      }" class="activity-type-priority-input condition-number-input" value="${
        jsonData.priority
      }" placeholder="优先级" style="width: 54px;height: ${conditionRowInnerHeight}px;"/>
        ${selects.length ? diejiaSelects : ''}
      </div>
    `;

      // 只监听一次事件
      document.body.removeEventListener('change', changeEvtfn);
      document.body.removeEventListener('input', inputEvtfn);
      document.body.removeEventListener('blur', blurEvtfn, true);
      document.body.addEventListener('change', changeEvtfn);
      document.body.addEventListener('input', inputEvtfn);
      document.body.addEventListener('blur', blurEvtfn, true);

      return group!.addShape('dom', {
        attrs: {
          width: cfg!.size![0],
          height: cfg!.size![1],
          // 传入 DOM 的 html
          html: `
          <div>
            <div style="background-color: #f2f2f2;border-radius:4px;height: 100%;">
              <div style="background-color: ${
                cfg!.style!.titleBgColor
              }; color: #fff; font-size: 12px;text-align: center;border-radius:4px;height: ${conditionTitleHeight}px;line-height: ${conditionTitleHeight}px;">
              ${cfg!.title}
              </div>
              <div style="padding: ${conditionContentPaddingY}px ${conditionContentPaddingY * 2}px;">
                <div style="display: flex;">
                  ${conditionHtml}
                </div>
                <div style="height: ${splitLineHeight}px;margin: ${splitLineMargin}px 0;background-color: ${
            cfg!.isPre ? '#d70a25' : 'var(--oio-button-primary-background)'
          };">
                </div>
                <textarea 
                  class="activity-textarea"
                  data-id="${cfg!.id}"
                  placeholder="活动规则" 
                  disabled
                  style="width: 100%;height: ${textareaHeight}px;border-radius: 4px;padding: 3px;resize: none;" 
                  rows="3">${generateActivityRuleStr(jsonData)}</textarea>
              </div>
            </div>
            ${generateActionBar({ ...jsonData, ...cfg })}
          </div>
        `
        }
      });
    }
  });
};
